import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { debounceTime } from 'rxjs';

// --- Interfaces ---
import type { ICategories } from '@interfaces/category';
import type { AttachFileData } from '@interfaces/file';
import type {
	IPicture,
	IProduct,
	IProducts,
	IVideo,
} from '@interfaces/product';

export interface IGetProductsParams {
	userId?: number;
	productId?: number;
	storeId?: number;
	priceListId?: number;
	maxPrice?: number;
	minPrice?: number;
	search?: string;
	showOutOfStock?: boolean;
	featured?: boolean;
	isVariation?: boolean;
	simpleProductsOnly?: boolean;
	inactive?: boolean;
	orderBy?:
		| 'Default'
		| 'Newest'
		| 'Oldest'
		| 'MostSold'
		| 'LowestPrice'
		| 'GreatestPrice'
		| 'NameAsc'
		| 'NameDesc'
		| 'SkuAsc'
		| 'SkuDesc';
	brands?: number[];
	categories?: number[];
	tenantId?: number;
	page?: number;
	pageLimit?: number;
	totalPages?: number;
}

@Injectable({
	providedIn: 'root',
})
export class ProductsService {
	constructor(private readonly _http: HttpClient) {}

	getProducts(queryParams?: IGetProductsParams) {
		return this._http.get<IProducts>('products', {
			params: (<unknown>queryParams) as HttpParams,
		});
	}

	getSimplifiedProducts(queryParams?: IGetProductsParams) {
		return this._http
			.get<IProducts>('products/simplified', {
				params: (<unknown>queryParams) as HttpParams,
			})
			.pipe(debounceTime(500));
	}

	getProductsByCategory(queryParams?: IGetProductsParams) {
		return this._http.get<ICategories>('products/categories', {
			params: (<unknown>queryParams) as HttpParams,
		});
	}

	getProductById(id: IProduct['id'], isEditing?: boolean) {
		const query = isEditing ? '?promotionalPrice=false' : '';
		return this._http.get<IProduct>(`products/${id}${query}`);
	}

	postProduct(product: IProduct) {
		return this._http.post<IProduct>(`products`, product);
	}

	updateProduct(product: IProduct) {
		return this._http.put<IProduct>(`products/${product.id}`, product);
	}

	updateSale(id: IProduct['id'], sale: any) {
		return this._http.put<IProduct>(`products/${id}/sale`, sale);
	}

	deleteProduct(id: IProduct['id']) {
		return this._http.delete<IProduct>(`products/${id}`);
	}

	activate(id: IProduct['id']) {
		return this._http.post<IProduct>(`products/${id}/activate`, {});
	}

	postProductPicture(id: IProduct['id'], file: AttachFileData, data: any) {
		const formData = new FormData();
		formData.append('file', file.file, file.filename);
		formData.append('description', data.description);
		formData.append('exhibitionOrder', data.exhibitionOrder);

		return this._http.post<IPicture>(`products/${id}/pictures`, formData);
	}

	savePicture(productId: IProduct['id'], pictureId: number) {
		return this._http.post<IPicture>(
			`products/${productId}/pictures/${pictureId}`,
			{ productId, pictureId }
		);
	}

	deletePrice(productId: IProduct['id'], priceId: number) {
		return this._http.delete<IProduct>(
			`products/${productId}/prices/${priceId}`
		);
	}

	deleteStock(productId: IProduct['id'], stockId: number) {
		return this._http.delete<IProduct>(
			`products/${productId}/stocks/${stockId}`
		);
	}

	deletePicture(pictureId: number) {
		return this._http.delete<IPicture>(`products/pictures/${pictureId}`);
	}

	postVideo(id: IProduct['id'], video: IVideo) {
		return this._http.post<IVideo>(`products/${id}/videos`, video);
	}

	putVideo(id: IProduct['id'], videoId: number, video: IVideo) {
		return this._http.put<IVideo>(`products/${id}/videos/${videoId}`, video);
	}

	deleteVideo(videoId: number) {
		return this._http.delete<IVideo>(`products/videos/${videoId}`);
	}
}
